// Subject files - the heart of the persona-first layout. One card per
// patient/case/employee (vertical-aware noun), composed ONLY from recordings
// the caller may see (server-enforced visibility).
import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardActionArea, CardContent, Chip, Grid, Stack, TextField, Typography,
} from '@mui/material';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import { useNavigate } from 'react-router-dom';
import lpsService from '../services/lpsService';
import { nounsFor } from '../utils/verticalNouns';

const fmtDur = (s) => (s ? `${Math.floor(s / 60)}m` : '—');
const fmtDate = (iso) => (iso || '').slice(0, 10);

export default function SubjectsPage() {
  const [items, setItems] = useState([]);
  const [nouns, setNouns] = useState(nounsFor('general'));
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    lpsService.listSubjects().then(setItems).catch(() => setItems([]));
    lpsService.getVertical().then((v) => setNouns(nounsFor(v.vertical))).catch(() => {});
  }, []);

  const visible = items.filter((s) =>
    !search || (s.subject_id || '').toLowerCase().includes(search.toLowerCase()));

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>{nouns.subjects}</Typography>
        <TextField size="small" placeholder={`Search ${nouns.subject.toLowerCase()} ID…`}
          value={search} onChange={(e) => setSearch(e.target.value)} />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Every {nouns.subject.toLowerCase()} file gathers their {nouns.sessions.toLowerCase()},
        reports and progress trends — you only see files your role permits.
      </Typography>
      <Grid container spacing={2}>
        {visible.map((s) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={s.key || s.subject_id}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardActionArea disabled={!s.key} sx={{ height: '100%' }}
                onClick={() => s.key && navigate(`/subjects/${encodeURIComponent(s.key)}`)}>
                <CardContent>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                    <FolderSharedIcon color="primary" />
                    <Typography fontWeight={700}>{s.subject_id}</Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} sx={{ mb: 1 }} flexWrap="wrap">
                    <Chip size="small" label={`${s.sessions} ${s.sessions === 1 ? nouns.session.toLowerCase() : nouns.sessions.toLowerCase()}`} />
                    {s.totalSeconds > 0 && <Chip size="small" variant="outlined" label={fmtDur(s.totalSeconds)} />}
                    {(s.departments || []).slice(0, 1).map((d) => (
                      <Chip size="small" variant="outlined" key={d} label={d} />
                    ))}
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {fmtDate(s.firstAt)} → {fmtDate(s.lastAt)}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
        {!visible.length && (
          <Grid item xs={12}>
            <Typography color="text.secondary" align="center" sx={{ p: 6 }}>
              No {nouns.subjects.toLowerCase()} yet — upload a {nouns.session.toLowerCase()} with a
              {' '}{nouns.subject.toLowerCase()} ID and the file appears here.
            </Typography>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
